import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SocketContext } from "./SocketContext";
import { WebsocketEventType } from "./types";
import { createSocketConnection, getSurvivorIdFromPayload, refreshGroupSubscriptions, disconnectSocket, } from "./socket-client";
/**
 * Maps WebSocket event types to query key resource names to invalidate.
 * User-specific events use resource names that match query keys like ["user", id, "resource"].
 * A resource may contain `/` to address a nested key — e.g. 'goal/linked-items'
 * matches ["user", id, "goal", "linked-items", ...] without also invalidating
 * every ["user", id, "goal"] query the way a bare 'goal' would.
 * Group events use full query key arrays.
 *
 * Exported for tests (shared/src/websocket/SocketProvider.test.ts pins the
 * ISS-260717-fbha rows below).
 */
export const eventToQueryKeys = {
    // User-specific events - invalidate queries matching ["user", survivorId, resource].
    // 'goal/linked-items' keeps the dashboard goals list's batched counts
    // (ISS-260717-fbha) live on realtime reminder/nudge changes — its key sits
    // under the "goal" segment, so the 'reminder'/'nudge' resources never
    // reach it (Codex review finding 2a).
    [WebsocketEventType.ReminderCreated]: {
        resources: ["reminder", "nudge", "goal/linked-items"],
    },
    [WebsocketEventType.ReminderUpdated]: {
        resources: ["reminder", "nudge", "goal/linked-items"],
    },
    [WebsocketEventType.ReminderDeleted]: {
        resources: ["reminder", "nudge", "goal/linked-items"],
    },
    [WebsocketEventType.ReminderEventCreated]: { resources: ["reminder-event"] },
    [WebsocketEventType.TaskCreated]: { resources: ["task"] },
    [WebsocketEventType.TaskUpdated]: { resources: ["task"] },
    [WebsocketEventType.TaskDeleted]: { resources: ["task"] },
    [WebsocketEventType.JournalEntryCreated]: {
        resources: ["journal-entry", "journal"],
    },
    [WebsocketEventType.JournalEntryUpdated]: {
        resources: ["journal-entry", "journal"],
    },
    [WebsocketEventType.JournalEntryDeleted]: {
        resources: ["journal-entry", "journal"],
    },
    [WebsocketEventType.EventCreated]: { resources: ["event"] },
    [WebsocketEventType.UserProfileUpdated]: { queryKeys: [["user"]] },
    // Group events - invalidate specific query keys
    [WebsocketEventType.GroupCreated]: {
        queryKeys: [["client-groups"], ["subscription"]],
    },
    [WebsocketEventType.GroupUpdated]: { queryKeys: [["client-groups"]] },
    [WebsocketEventType.GroupDeleted]: {
        queryKeys: [["client-groups"], ["subscription"]],
    },
};
/**
 * Helper to check if a query key matches for user-specific data invalidation.
 * Supports two patterns:
 * - Array pattern (legacy): ["user", userId, "resource", ...] — a resource
 *   containing `/` matches successive key segments, so 'goal/linked-items'
 *   matches ["user", userId, "goal", "linked-items", ...] but NOT
 *   ["user", userId, "goal", ...].
 * - URL pattern (centralized): ["/user/{userId}/resource", ...]
 *
 * Exported for tests.
 */
export const matchesUserQuery = (queryKey, survivorId, resources) => {
    if (!Array.isArray(queryKey) || queryKey.length < 1)
        return false;
    const firstElement = queryKey[0];
    // URL-based pattern from centralized query keys: ["/user/{userId}/resource", ...]
    if (typeof firstElement === "string" && firstElement.startsWith("/user/")) {
        return resources.some((name) => firstElement.startsWith(`/user/${survivorId}/${name}`));
    }
    // Array-based pattern (legacy): ["user", userId, "resource", ...]
    if (queryKey.length >= 3 &&
        firstElement === "user" &&
        queryKey[1] === survivorId) {
        return resources.some((name) => {
            const segments = name.split("/");
            return (queryKey.length >= 2 + segments.length &&
                segments.every((segment, index) => queryKey[2 + index] === segment));
        });
    }
    return false;
};
export const SocketProvider = ({ children, wsUrl, getToken, isAuthenticated, onEvent: externalOnEvent, onSocketError, }) => {
    const queryClient = useQueryClient();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);
    const onSocketErrorRef = useRef(onSocketError);
    useEffect(() => {
        onSocketErrorRef.current = onSocketError;
    }, [onSocketError]);
    const refreshGroups = useCallback(() => {
        refreshGroupSubscriptions(socketRef.current);
    }, []);
    const handleEvent = useCallback((event) => {
        // Call external handler if provided (e.g., for SWR invalidation)
        externalOnEvent?.(event);
        const mapping = eventToQueryKeys[event.type];
        if (!mapping) {
            return;
        }
        // Handle group/global events with specific query keys
        if (mapping.queryKeys) {
            mapping.queryKeys.forEach((queryKey) => {
                queryClient.invalidateQueries({ queryKey });
            });
            // When groups change, refresh our group room subscriptions
            if (event.type === WebsocketEventType.GroupCreated ||
                event.type === WebsocketEventType.GroupDeleted) {
                refreshGroups();
            }
            return;
        }
        // Handle user-specific events with resource matching
        if (mapping.resources) {
            const survivorId = getSurvivorIdFromPayload(event.payload);
            if (survivorId) {
                queryClient.invalidateQueries({
                    predicate: (query) => matchesUserQuery(query.queryKey, survivorId, mapping.resources),
                });
            }
        }
    }, [queryClient, refreshGroups, externalOnEvent]);
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        let isMounted = true;
        const connectSocket = async () => {
            try {
                const newSocket = await createSocketConnection({
                    url: wsUrl,
                    getToken,
                    onConnect: () => {
                        if (isMounted) {
                            setIsConnected(true);
                        }
                    },
                    onDisconnect: () => {
                        if (isMounted) {
                            setIsConnected(false);
                        }
                    },
                    onError: (detail) => {
                        onSocketErrorRef.current?.(detail);
                    },
                    onEvent: handleEvent,
                });
                if (isMounted) {
                    socketRef.current = newSocket;
                    setSocket(newSocket);
                }
                else {
                    // Component unmounted during connection, clean up
                    disconnectSocket(newSocket);
                }
            }
            catch (error) {
                console.error("[Socket] Failed to connect:", error);
            }
        };
        connectSocket();
        return () => {
            isMounted = false;
            disconnectSocket(socketRef.current);
            socketRef.current = null;
        };
    }, [isAuthenticated, wsUrl, getToken, handleEvent]);
    return (_jsx(SocketContext.Provider, { value: { socket, isConnected, refreshGroups }, children: children }));
};
